/**
 * GlJournalVoucher — Printable transaction voucher for GL journals.
 *
 * Rendered inside MasterReportPreview (which handles the print modal, @media print
 * isolation, and the Print button). Pass the full GlJournalDto.
 */

import type { ReactNode } from 'react';
import type { GlJournalDto, GlJournalLineDto } from '@/types/glJournal.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '110px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:    { bg: '#f3f4f6', text: '#374151' },
    Pending:  { bg: '#fef3c7', text: '#92400e' },
    Approved: { bg: '#d1fae5', text: '#065f46' },
    Rejected: { bg: '#fee2e2', text: '#991b1b' },
    Posted:   { bg: '#dbeafe', text: '#1e40af' },
    Cancelled:{ bg: '#f3f4f6', text: '#6b7280' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface GlJournalVoucherProps {
  journal:     GlJournalDto;
  companyName: string;
  printedBy?:  string;
}

export const GlJournalVoucher = ({
  journal,
  companyName,
  printedBy,
}: GlJournalVoucherProps): ReactNode => {

  const totalDebit  = journal.totalDebit  ?? 0;
  const totalCredit = journal.totalCredit ?? 0;

  // ── Line columns ─────────────────────────────────────────────────────────
  const lineColumns = [
    { key: 'lineNumber', label: '#',           width: '30px',  align: 'center' as const,
      render: (l: GlJournalLineDto) => l.lineNumber },
    { key: 'accountCode', label: 'Acc. Code',  width: '80px',
      render: (l: GlJournalLineDto) => l.accountCode || '—' },
    { key: 'accountName', label: 'Account Name',
      render: (l: GlJournalLineDto) => l.accountName || '—' },
    { key: 'narration',   label: 'Narration',
      render: (l: GlJournalLineDto) => l.narration || '' },
    { key: 'debitAmount', label: 'Debit',      width: '100px', align: 'right' as const,
      render: (l: GlJournalLineDto) => l.debitAmount > 0 ? fmtNum(l.debitAmount) : '' },
    { key: 'creditAmount',label: 'Credit',     width: '100px', align: 'right' as const,
      render: (l: GlJournalLineDto) => l.creditAmount > 0 ? fmtNum(l.creditAmount) : '' },
  ];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left: Company + title */}
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              {journal.transactionTypeCode === 'JV' ? 'Journal Voucher' :
               journal.transactionTypeCode === 'AJ' ? 'Adjusting Journal Entry' :
               journal.transactionTypeCode === 'RV' ? 'Reversal Voucher' :
               journal.transactionTypeCode === 'OB' ? 'Opening Balance Entry' :
               'General Ledger Voucher'}
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>

          {/* Right: Document number + status */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {journal.documentNumber ?? 'DRAFT'}
            </div>
            <div style={{ marginTop: '4px' }}>
              <StatusBadge status={journal.workflowStatus} />
              {' '}
              <StatusBadge status={journal.postingStatus} />
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Document details ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <LabelValue label="Document Date"   value={fmtDate(journal.documentDate)} />
          <LabelValue label="Posting Date"    value={fmtDate(journal.postingDate)} />
          <LabelValue label="Financial Year"  value={journal.financialYearCode ?? '—'} />
          <LabelValue label="Period"          value={journal.financialPeriodCode ?? '—'} />
          <LabelValue label="Reference"       value={journal.reference || '—'} />
        </div>
        <div>
          <LabelValue label="Currency"        value={`${journal.currencyCode} (Rate: ${journal.exchangeRate?.toFixed(4) ?? '1.0000'})`} />
          <LabelValue label="Transaction Type" value={journal.transactionTypeCode ?? '—'} />
          <LabelValue label="Total Debit"     value={<strong>{fmtNum(totalDebit)}</strong>} />
          <LabelValue label="Total Credit"    value={<strong>{fmtNum(totalCredit)}</strong>} />
          <LabelValue label="Balanced"        value={journal.isBalanced ? '✓ Yes' : '✗ No'} />
        </div>
      </div>

      {/* Narration */}
      {journal.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {journal.narration}
        </div>
      )}

      {/* ── Lines table ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Journal Entries
      </div>

      <PrintableTable
        columns={lineColumns}
        rows={journal.lines ?? []}
        rowKey={(l) => l.id}
      />

      {/* Totals row */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '2px solid #1e3a5f',
        paddingTop: '6px',
        marginTop: '-8px',
        marginBottom: '20px',
        gap: '0',
      }}>
        <div style={{ textAlign: 'right', width: '100px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', paddingRight: '8px' }}>
          {fmtNum(totalDebit)}
        </div>
        <div style={{ textAlign: 'right', width: '100px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', paddingRight: '8px' }}>
          {fmtNum(totalCredit)}
        </div>
      </div>

      {/* ── Workflow / Approval Trail ─────────────────────────────────────────── */}
      {journal.workflowHistory && journal.workflowHistory.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
            Approval Trail
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#1e3a5f' }}>
                <th style={{ color: '#fff', padding: '4px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Date/Time</th>
                <th style={{ color: '#fff', padding: '4px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Action</th>
                <th style={{ color: '#fff', padding: '4px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>By</th>
                <th style={{ color: '#fff', padding: '4px 8px', textAlign: 'left', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {journal.workflowHistory.map((h, i) => (
                <tr key={h.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                  <td style={{ padding: '4px 8px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{fmtDateTime(h.actionDate)}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{h.action}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{h.actionByName || h.actionBy || '—'}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{h.remarks || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {[
          { label: 'Prepared By', value: journal.createdByName || '' },
          { label: 'Approved By', value: journal.approvedByName || '' },
          { label: 'Posted By',   value: journal.postedByName   || '' },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
            {value && (
              <div style={{ fontSize: '10px', color: '#374151', marginTop: '2px' }}>{value}</div>
            )}
          </div>
        ))}
      </div>

    </div>
  );
};
